// File: frontend/src/pages/UpdatePasswordPage.js
import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate, Link } from 'react-router-dom';
import { toast, Toaster } from 'react-hot-toast';
import './LoginPage.css'; // Re-using the same CSS for a consistent look

function UpdatePasswordPage() {
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  // This effect listens for the special 'PASSWORD_RECOVERY' event
  // which happens when Supabase detects the recovery token in the URL.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        console.log('Password recovery event detected.');
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setError('');

    // 1. Check if passwords match
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    
    // 2. Check for password length (optional but recommended)
    if (password.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }

    setLoading(true);

    try {
      // 3. This is the Supabase function to update the user's password
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) throw error;

      toast.success('Password updated successfully! You can now log in.');
      setSuccess(true);
      
      // Log the user out of this "recovery" session
      await supabase.auth.signOut();
      
      // Redirect to login after a short delay
      setTimeout(() => {
        navigate('/login');
      }, 3000);

    } catch (error) {
      console.error('Error updating password:', error);
      setError(error.error_description || error.message);
      toast.error(error.error_description || error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <Toaster position="top-center" reverseOrder={false} />

      <div className="login-box">
        <img src="/logo_fullmockup.png" alt="FullMockup.com Logo" className="login-logo" />

        {success ? (
          // --- 1. SUCCESS STATE ---
          <div className="forgot-password-success">
            <h2>Password Updated!</h2>
            <p>Your password has been changed successfully.</p>
            <p>Redirecting you to the login page...</p>
            <Link to="/login" className="login-button email-button" style={{marginTop: '1.5rem', textDecoration: 'none'}}>
              Back to Login
            </Link>
          </div>

        ) : (
          // --- 2. DEFAULT STATE ---
          <>
            <h2 className="forgot-password-title">Set a New Password</h2>
            <p className="forgot-password-subtitle">
              Enter your new password below.
            </p>

            <form onSubmit={handlePasswordUpdate}>
              <div className="input-group">
                <label htmlFor="password">New Password</label>
                <input
                  id="password"
                  type="password"
                  placeholder="Enter your new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <div className="input-group">
                <label htmlFor="confirm-password">Confirm New Password</label>
                <input
                  id="confirm-password"
                  type="password"
                  placeholder="Confirm your new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              
              {/* Show password mismatch errors */}
              {error && <p className="error-message">{error}</p>}
              
              <button 
                className="login-button email-button" 
                type="submit" 
                disabled={loading}
              >
                {loading ? (
                  <div className="spinner-small"></div>
                ) : (
                  'Update Password'
                )}
              </button>
            </form>
          </>
        )}

      </div>
    </div>
  );
}

export default UpdatePasswordPage;